import { AllFilters } from './../../../../../shared/models/all-filter.model';
import { inject } from '@angular/core';
import {
  HealthRecordDto,
  HealthRecordsService,
  PaginationResult,
  UsersService,
  VeterinarianDto,
} from '@anihis/core/data-access';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
  withHooks,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { LoadingStatus } from '../../../../../shared/enums/loading';
import { initialFilters } from 'apps/anihis-portal/src/app/shared/models/all-initial-filters';
import { DateTime } from 'luxon';
import { finalize, tap } from 'rxjs/operators';
import { SignalRService } from '../../../../../shared/services/signalr.service';

export type ReportsQuery = {
  pageNumber: number;
  pageSize: number;
  filters: AllFilters;
  pagination: PaginationResult | undefined;
};

type ReportState = {
  reports: HealthRecordDto[] | undefined;
  veterinarians: VeterinarianDto[] | undefined;
  animalUid: string | undefined;
  status: LoadingStatus | undefined;
  statusSearch: LoadingStatus | undefined;
  pagination: PaginationResult | undefined;
  query: ReportsQuery;
};

const emptyQuery: ReportsQuery = {
  pageNumber: 1,
  pageSize: 15,
  filters: initialFilters,
  pagination: undefined,
};

const initialState: ReportState = {
  reports: undefined,
  veterinarians: undefined,
  animalUid: undefined,
  status: undefined,
  statusSearch: undefined,
  pagination: undefined,
  query: emptyQuery,
};

export const ReportStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      healthRecordsService = inject(HealthRecordsService),
      usersService = inject(UsersService),
    ) => {
      return {
        setAnimalUid(uid: string) {
          patchState(store, { animalUid: uid });
        },

        toStartOfDay(date: Date | null | undefined): Date | undefined {
          return date
            ? DateTime.fromJSDate(date).startOf('day').toJSDate()
            : undefined;
        },

        toEndOfDay(date: Date | null | undefined): Date | undefined {
          return date
            ? DateTime.fromJSDate(date).endOf('day').toJSDate()
            : undefined;
        },

        fetchRecord() {
          const { filters, pageSize } = store.query();
          patchState(store, { status: LoadingStatus.Loading });
          return healthRecordsService
            .healthRecordsGet(
              filters.search,
              this.toStartOfDay(filters.range?.start),
              this.toEndOfDay(filters.range?.end),
              store.animalUid(),
              filters.veterinarian,
              undefined,
              1,
              pageSize,
            )
            .pipe(
              tap((res) => {
                patchState(store, {
                  reports: res.healthRecords,
                  pagination: {
                    pageNumber: res.pageNumber,
                    pageSize: res.pageSize,
                    totalCount: res.totalCount,
                    totalPages: res.totalPages,
                    hasNextPage: res.hasNextPage,
                    hasPreviousPage: res.hasPreviousPage,
                  },
                });
              }),
              finalize(() =>
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                }),
              ),
            );
        },

        updateDateRangeFilter(
          start: Date | null | undefined,
          end: Date | null | undefined,
        ) {
          patchState(store, {
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                range: start && end ? { start, end } : undefined,
              },
            },
          });
          this.fetchRecord().subscribe();
        },

        updateSearch(search: string) {
          patchState(store, {
            statusSearch: LoadingStatus.Loading,
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                search,
              },
            },
          });
          this.fetchRecord().subscribe();
        },

        appendData(
          newHealthRecords: HealthRecordDto[],
          pagination: PaginationResult,
        ) {
          const current = store.reports() ?? [];
          const existingUids = new Set(current.map((r) => r.healthRecordUid));

          const uniqueNew = newHealthRecords.filter(
            (r) => !existingUids.has(r.healthRecordUid),
          );

          patchState(store, {
            reports: [...current, ...uniqueNew],
            status: LoadingStatus.Loaded,
            pagination: {
              pageNumber: pagination.pageNumber,
              pageSize: pagination.pageSize,
              totalCount: pagination.totalCount,
              totalPages: pagination.totalPages,
              hasNextPage: pagination.hasNextPage,
              hasPreviousPage: pagination.hasPreviousPage,
            },
          });
        },

        loadMoreData() {
          const { filters, pageSize } = store.query();
          const nextPage = store.pagination()?.pageNumber! + 1;
          patchState(store, { status: LoadingStatus.Loading });
          return healthRecordsService
            .healthRecordsGet(
              filters.search,
              this.toStartOfDay(filters.range?.start),
              this.toEndOfDay(filters.range?.end),
              store.animalUid(),

              filters.veterinarian,
              undefined,
              nextPage,
              pageSize,
            )
            .pipe(
              tap((res) => {
                this.appendData(res.healthRecords ?? [], {
                  pageNumber: res.pageNumber ?? nextPage,
                  pageSize: res.pageSize ?? pageSize,
                  totalCount: res.totalCount ?? 0,
                  totalPages: res.totalPages ?? nextPage,
                  hasNextPage: res.hasNextPage ?? false,
                  hasPreviousPage: res.hasPreviousPage ?? false,
                });
              }),
              finalize(() =>
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                }),
              ),
            );
        },
        updateVeterinariansFilter(veterinarian: string) {
          patchState(store, {
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                veterinarian: veterinarian,
              },
            },
          });
          this.fetchRecord().subscribe();
        },

        fetchVeterinarians: rxMethod<void>(() => {
          return usersService.usersVeterinariansGet(false).pipe(
            tap((res) => {
              patchState(store, {
                veterinarians: res.veterinans,
                status: LoadingStatus.Loaded,
              });
            }),
            finalize(() =>
              patchState(store, {
                status: LoadingStatus.Loaded,
                statusSearch: LoadingStatus.Loaded,
              }),
            ),
          );
        }),
      };
    },
  ),
  withHooks({
    onInit(store, signalRService = inject(SignalRService)) {
      store.fetchVeterinarians();

      signalRService.healthRecordVisibilityChanged$.subscribe((data) => {
        const currentAnimalUid = store.animalUid();
        if (!currentAnimalUid || data.animalUid !== currentAnimalUid) {
          return;
        }

        store.fetchRecord().subscribe();
      });
    },
  }),
);
